import { modelOptions, post, prop } from "@typegoose/typegoose";
import type { DataTypeSpecifier, SensorType } from "verdi-common";
import { z } from "zod";
import {
    DataTypeSpecifierString,
    getZodSchemaFromClass,
    SensorTypeString,
    validate,
} from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import {
    SupplementalZoneDataStreamFieldsWithDefaults,
    SupplementalZoneDataStreamSchemaTypes,
} from "../../types/supplementalZoneDataStream.d.js";
import { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "SupplementalZoneDataStream" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SupplementalZoneDataStream>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SupplementalZoneDataStream extends SchemaBase<
    SupplementalZoneDataStream,
    SupplementalZoneDataStreamFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: true, default: "" })
    sourceID: string = "";

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: true, default: "" })
    name: string = "";

    @validate({ zodValidator: SensorTypeString.or(z.literal("").optional()) })
    @prop({ type: () => String, required: true, default: "" })
    sensorType: SensorType = "" as SensorType;

    @validate({ zodValidator: DataTypeSpecifierString.or(z.literal("").optional()) })
    @prop({ type: () => String, required: true, default: "" })
    dataType: DataTypeSpecifier = "" as DataTypeSpecifier;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    startDate?: Date;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    endDate?: Date;

    static dtoValidators: DtoValidatorTypes<SupplementalZoneDataStreamSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SupplementalZoneDataStream, SupplementalZoneDataStreamFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SupplementalZoneDataStream.dtoValidators = getZodSchemaFromClass(
    SupplementalZoneDataStream,
) as unknown as DtoValidatorTypes<SupplementalZoneDataStreamSchemaTypes>;
